import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Loader2, ArrowRight, Building2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneNumber } from "@/lib/utils";

interface CompanyData {
  name?: string;
  dbaName?: string;
  email?: string;
  phone?: string;
  address?: string;
  mailingAddress?: string;
  dotNumber?: string;
  carrierOperation?: string;
  classDefinition?: string;
  carship?: string;
  powerUnits?: string;
  truckUnits?: string;
  busUnits?: string;
  totalDrivers?: string;
  totalCdl?: string;
  driverInterTotal?: string;
  fleetSize?: string;
  hmIndicator?: string;
  mcs150Date?: string;
  mcs150Mileage?: string;
  mcs150MileageYear?: string;
  addDate?: string;
  interstateBeyond100Miles?: string;
  interstateWithin100Miles?: string;
  intrastateBeyond100Miles?: string;
  intrastateWithin100Miles?: string;
  businessOrgDescription?: string;
  cargoGenFreight?: string;
  cargoLogPole?: string;
}

interface FieldSelection {
  name: boolean;
  dbaName: boolean;
  email: boolean;
  phone: boolean;
  address: boolean;
  mailingAddress: boolean;
  carrierOperation: boolean;
  classDefinition: boolean;
  carship: boolean;
  powerUnits: boolean;
  truckUnits: boolean;
  busUnits: boolean;
  totalDrivers: boolean;
  totalCdl: boolean;
  driverInterTotal: boolean;
  fleetSize: boolean;
  hmIndicator: boolean;
  mcs150Date: boolean;
  mcs150Mileage: boolean;
  mcs150MileageYear: boolean;
  addDate: boolean;
  interstateBeyond100Miles: boolean;
  interstateWithin100Miles: boolean;
  intrastateBeyond100Miles: boolean;
  intrastateWithin100Miles: boolean;
  businessOrgDescription: boolean;
  cargoGenFreight: boolean;
  cargoLogPole: boolean;
}

interface DOTCompanyRefreshModalProps {
  open: boolean;
  onClose: () => void;
  company: CompanyData & { id: number };
  onApply: (data: Partial<CompanyData> & { rawResponse?: any }) => void;
  onFetchSuccess?: (rawResponse: any) => void;
  isUpdating?: boolean;
}

export default function DOTCompanyRefreshModal({
  open,
  onClose,
  company,
  onApply,
  onFetchSuccess,
  isUpdating = false
}: DOTCompanyRefreshModalProps) {
  const { toast } = useToast();
  const [dotData, setDotData] = useState<CompanyData | null>(null);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [fieldSelection, setFieldSelection] = useState<FieldSelection>({
    name: true,
    dbaName: true,
    email: true,
    phone: true,
    address: true,
    mailingAddress: true,
    carrierOperation: true,
    classDefinition: true,
    carship: true,
    powerUnits: true,
    truckUnits: true,
    busUnits: true,
    totalDrivers: true,
    totalCdl: true,
    driverInterTotal: true,
    fleetSize: true,
    hmIndicator: true,
    mcs150Date: true,
    mcs150Mileage: true,
    mcs150MileageYear: true,
    addDate: true,
    interstateBeyond100Miles: true,
    interstateWithin100Miles: true,
    intrastateBeyond100Miles: true,
    intrastateWithin100Miles: true,
    businessOrgDescription: true,
    cargoGenFreight: true,
    cargoLogPole: true,
  });

  const fetchMutation = useMutation({
    mutationFn: async () => {
      if (!company.dotNumber) {
        throw new Error("DOT number is required to fetch DOT profile data");
      }

      const response = await apiRequest("POST", "/api/dot/fetch-company", {
        dotNumber: company.dotNumber,
      });

      const data = await response.json();
      const { rawResponse: raw, ...companyData } = data;
      return { companyData: companyData as CompanyData, rawResponse: raw };
    },
    onSuccess: (result) => {
      setDotData(result.companyData);
      setRawResponse(result.rawResponse);
      onFetchSuccess?.(result.rawResponse);
      toast({
        title: "Success",
        description: "DOT profile data fetched successfully. Review and select fields to update.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch DOT profile data",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (open && company.dotNumber && !dotData && !fetchMutation.isPending) {
      fetchMutation.mutate();
    }
  }, [open, company.dotNumber]);

  useEffect(() => {
    if (!open) {
      setDotData(null);
      setRawResponse(null);
    }
  }, [open]);

  const handleApply = () => {
    if (!dotData) return;

    const updateData: Partial<CompanyData> = {};

    Object.keys(fieldSelection).forEach((key) => {
      const fieldKey = key as keyof FieldSelection;
      if (fieldSelection[fieldKey] && dotData[fieldKey] !== undefined) {
        (updateData as any)[fieldKey] = dotData[fieldKey];
      }
    });

    if (rawResponse) {
      (updateData as any).rawResponse = rawResponse;
    }
    onApply(updateData);
  };

  const toggleField = (field: keyof FieldSelection) => {
    setFieldSelection(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const selectAll = () => {
    const allSelected: FieldSelection = {} as FieldSelection;
    Object.keys(fieldSelection).forEach(key => {
      allSelected[key as keyof FieldSelection] = true;
    });
    setFieldSelection(allSelected);
  };

  const selectNone = () => {
    const noneSelected: FieldSelection = {} as FieldSelection;
    Object.keys(fieldSelection).forEach(key => {
      noneSelected[key as keyof FieldSelection] = false;
    });
    setFieldSelection(noneSelected);
  };

  const normalizeValue = (value: string | undefined | null): string => {
    return value?.trim() || "";
  };

  const hasChanges = () => {
    if (!dotData) return false;
    return Object.keys(fieldSelection).some(key => {
      const fieldKey = key as keyof FieldSelection;
      const currentNormalized = normalizeValue(company[fieldKey]);
      const dotNormalized = normalizeValue(dotData[fieldKey]);
      return fieldSelection[fieldKey] && dotNormalized !== currentNormalized;
    });
  };

  const renderFieldComparison = (
    label: string,
    field: keyof FieldSelection,
    currentValue: string | undefined | null,
    newValue: string | undefined | null
  ) => {
    const currentNormalized = normalizeValue(currentValue);
    const newNormalized = normalizeValue(newValue);
    const isDifferent = currentNormalized !== newNormalized;
    
    return (
      <div className="flex items-start gap-3 py-3 border-b last:border-b-0">
        <Checkbox
          id={field}
          checked={fieldSelection[field]}
          onCheckedChange={() => toggleField(field)}
          disabled={!isDifferent}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <label htmlFor={field} className="font-medium text-sm cursor-pointer">
            {label}
          </label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Current</p>
              <p className={`text-sm ${isDifferent ? 'bg-red-50 p-2 rounded border border-red-200' : 'bg-gray-50 p-2 rounded'}`}>
                {currentValue || <span className="text-gray-400 italic">Not set</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">From DOT Profile</p>
              <p className={`text-sm ${isDifferent ? 'bg-green-50 p-2 rounded border border-green-200' : 'bg-gray-50 p-2 rounded'}`}>
                {newValue || <span className="text-gray-400 italic">Not available</span>}
              </p>
            </div>
          </div>
          {isDifferent && (
            <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
              <ArrowRight className="w-3 h-3" />
              <span>Value will be updated if selected</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Refresh DOT Profile Data
          </DialogTitle>
        </DialogHeader>

        {fetchMutation.isPending && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Fetching DOT profile data...</span>
          </div>
        )}

        {fetchMutation.isError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-destructive font-medium">Failed to fetch DOT profile data</p>
            <p className="text-sm text-muted-foreground mt-2">
              {fetchMutation.error?.message || "Please try again later"}
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => fetchMutation.mutate()}
            >
              Retry
            </Button>
          </div>
        )}

        {dotData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Select which fields you want to update from the DOT profile.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={selectNone}>
                  Select None
                </Button>
              </div>
            </div>

            <Separator />

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {renderFieldComparison("Company Name", "name", company.name, dotData.name)}
                {renderFieldComparison("DBA Name", "dbaName", company.dbaName, dotData.dbaName)}
                {renderFieldComparison("Email", "email", company.email, dotData.email)}
                {renderFieldComparison("Phone", "phone", formatPhoneNumber(company.phone), formatPhoneNumber(dotData.phone))}
                {renderFieldComparison("Address", "address", company.address, dotData.address)}
                {renderFieldComparison("Mailing Address", "mailingAddress", company.mailingAddress, dotData.mailingAddress)}
                {renderFieldComparison("Business Organization", "businessOrgDescription", company.businessOrgDescription, dotData.businessOrgDescription)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Classification</CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {renderFieldComparison("Carrier Operation", "carrierOperation", company.carrierOperation, dotData.carrierOperation)}
                {renderFieldComparison("Class Definition", "classDefinition", company.classDefinition, dotData.classDefinition)}
                {renderFieldComparison("Carship", "carship", company.carship, dotData.carship)}
                {renderFieldComparison("Hazmat Indicator", "hmIndicator", company.hmIndicator, dotData.hmIndicator)}
                {renderFieldComparison("Cargo General Freight", "cargoGenFreight", company.cargoGenFreight, dotData.cargoGenFreight)}
                {renderFieldComparison("Cargo Log/Pole", "cargoLogPole", company.cargoLogPole, dotData.cargoLogPole)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Fleet & Drivers</CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {renderFieldComparison("Power Units", "powerUnits", company.powerUnits, dotData.powerUnits)}
                {renderFieldComparison("Truck Units", "truckUnits", company.truckUnits, dotData.truckUnits)}
                {renderFieldComparison("Bus Units", "busUnits", company.busUnits, dotData.busUnits)}
                {renderFieldComparison("Fleet Size", "fleetSize", company.fleetSize, dotData.fleetSize)}
                {renderFieldComparison("Total Drivers", "totalDrivers", company.totalDrivers, dotData.totalDrivers)}
                {renderFieldComparison("Total CDL", "totalCdl", company.totalCdl, dotData.totalCdl)}
                {renderFieldComparison("Interstate Drivers", "driverInterTotal", company.driverInterTotal, dotData.driverInterTotal)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Geographic Operations</CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {renderFieldComparison("Interstate Beyond 100 Miles", "interstateBeyond100Miles", company.interstateBeyond100Miles, dotData.interstateBeyond100Miles)}
                {renderFieldComparison("Interstate Within 100 Miles", "interstateWithin100Miles", company.interstateWithin100Miles, dotData.interstateWithin100Miles)}
                {renderFieldComparison("Intrastate Beyond 100 Miles", "intrastateBeyond100Miles", company.intrastateBeyond100Miles, dotData.intrastateBeyond100Miles)}
                {renderFieldComparison("Intrastate Within 100 Miles", "intrastateWithin100Miles", company.intrastateWithin100Miles, dotData.intrastateWithin100Miles)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">MCS-150 Information</CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {renderFieldComparison("MCS-150 Date", "mcs150Date", company.mcs150Date, dotData.mcs150Date)}
                {renderFieldComparison("MCS-150 Mileage", "mcs150Mileage", company.mcs150Mileage, dotData.mcs150Mileage)}
                {renderFieldComparison("MCS-150 Mileage Year", "mcs150MileageYear", company.mcs150MileageYear, dotData.mcs150MileageYear)}
                {renderFieldComparison("Add Date", "addDate", company.addDate, dotData.addDate)}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleApply}
                disabled={!hasChanges() || isUpdating}
              >
                {isUpdating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Apply Selected Changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
